import { useEffect, useMemo, useState } from 'react'
import { cachedGet } from '../../lib/api.js'
import { fullName } from '../../lib/employee.js'
import SearchableSelect from '../SearchableSelect.jsx'

// Shared employee combobox for the Employee-HR tabs — every tab's employee filter
// (bound to the section-wide filter in EmployeeFilter.jsx) plus the Leave add-row,
// Recurring target and Salary manual-add forms. Loads the roster once (cached
// across tabs) and renders the app's SearchableSelect, so all tabs match.
//
//   <EmployeePicker value={employeeId} onChange={setEmployeeId} />
//     value/onChange are STRING ids ('' = none). Options: {value:String(id),
//     label:code, hint:fullName·nickname·name_eng}.
//   allowAll  → prepend a "ทุกคน" ('' ) option (for filter bars).
//   branchId  → narrow the roster to one working branch.
//   activeOnly (default true) → active employees only.

const toOption = (e) => ({
  value: String(e.id),
  // Employee CODE is the label — it is what the roster is keyed on and what
  // HR types to find someone. The name/nickname stay in `hint`, which
  // SearchableSelect also matches on (so you can search by either) but does
  // not render. Mirrors the ลงเวลา picker.
  label: e.code || fullName(e),
  hint: [fullName(e), e.nickname, e.name_eng, e.active === false && 'พ้นสภาพ'].filter(Boolean).join(' · '),
})

export default function EmployeePicker({
  value = '',
  onChange,
  allowAll = false,
  allLabel = 'ทุกคน',
  branchId,
  activeOnly = true,
  placeholder = 'เลือกพนักงาน…',
  searchPlaceholder = 'พิมพ์รหัสพนักงาน / ชื่อ…',
  className = 'min-w-[220px]',
  disabled = false,
}) {
  const [employees, setEmployees] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [extra, setExtra] = useState(null)

  useEffect(() => {
    const params = { per_page: 500 }
    if (activeOnly) params.active = 'true'
    if (branchId) params.branch_id = branchId
    // cache key includes params, so branch-scoped and full rosters don't collide.
    cachedGet('/api/hr/employees', params)
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => setEmployees([]))
      .finally(() => setLoaded(true))
  }, [branchId, activeOnly])

  // The value can name someone this roster does not hold: the พนักงาน filter is shared
  // across tabs, so it may have been picked on รายชื่อ (which lists ex-staff) or be in
  // a branch this picker is narrowed away from. SearchableSelect shows the placeholder
  // for a value it has no option for — "ทุกคน" over a table filtered to one person. So
  // look the person up in the full roster (the cachedGet entry the other HR pages
  // already share) and add them as one extra option.
  const missing = loaded && Boolean(value) && !employees.some((e) => String(e.id) === String(value))
  useEffect(() => {
    if (!missing) { setExtra(null); return }
    let alive = true
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => {
        const all = Array.isArray(r) ? r : (r?.items || [])
        if (alive) setExtra(all.find((e) => String(e.id) === String(value)) || null)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [missing, value])

  const options = useMemo(() => {
    const opts = employees.map(toOption)
    if (extra) opts.push(toOption(extra))
    return allowAll ? [{ value: '', label: allLabel }, ...opts] : opts
  }, [employees, extra, allowAll, allLabel])

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyText="ไม่พบพนักงาน"
      className={className}
      disabled={disabled}
    />
  )
}
